import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChalkboardTeacher, LockKey, ShieldWarning, CheckCircle, WhatsappLogo } from "@phosphor-icons/react";

function formatPhoneBR(raw) {
  const d = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0,2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
}

export default function RegisterPage() {
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ref = (params.get("ref") || "").toUpperCase();
  const isInvite = !!ref;

  // Se já estiver autenticado, redireciona (ex.: back do navegador após registro)
  useEffect(() => {
    if (user && user.role) {
      if (user.must_change_password) nav("/change-password", { replace: true });
      else nav("/", { replace: true });
    }
  }, [user, nav]);

  const [inviteState, setInviteState] = useState(isInvite ? { loading: true } : null);
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: isInvite ? "student" : "teacher",
    linked_student_email: "",
    phone: "",
    whatsapp_consent: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isInvite) return;
    api.get(`/invite/${ref}`)
      .then(r => setInviteState({ loading: false, valid: true, teacher_name: r.data.teacher_name }))
      .catch(() => setInviteState({ loading: false, valid: false }));
  }, [ref, isInvite]);

  async function submit(e) {
    e.preventDefault();
    // validação extra para aluno: telefone + consentimento
    if (isInvite && form.role === "student") {
      const digits = (form.phone || "").replace(/\D/g, "");
      if (digits.length < 10 || digits.length > 11) {
        toast.error("Informe um número de WhatsApp válido com DDD (ex: (11) 99999-9999)");
        return;
      }
      if (!form.whatsapp_consent) {
        toast.error("Você precisa autorizar o recebimento de notificações para concluir o cadastro.");
        return;
      }
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (isInvite) payload.invite_code = ref;
      if (payload.role !== "parent") delete payload.linked_student_email;
      if (payload.role !== "student") {
        delete payload.phone;
        delete payload.whatsapp_consent;
      }
      await register(payload);
      toast.success("Conta criada!");
      nav("/");
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  }

  // Bloqueio: convite inválido
  if (isInvite && inviteState?.loading === false && !inviteState?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-[#F7F3EE]">
        <div className="max-w-md w-full card-elevated p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-[#FFE9EE] flex items-center justify-center mx-auto mb-4">
            <ShieldWarning size={32} color="#FF3D71" weight="duotone" />
          </div>
          <h1 className="font-heading text-2xl font-black text-[#12143A]">Acesso restrito</h1>
          <p className="text-zinc-600 mt-3 leading-relaxed">
            Por favor, solicite o link de cadastro diretamente ao seu professor.
          </p>
          <Link to="/login" data-testid="back-login" className="inline-block mt-6 text-[#1E5FFF] font-semibold underline">
            Já tenho uma conta — Entrar
          </Link>
        </div>
      </div>
    );
  }

  if (isInvite && inviteState?.loading) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400">Validando convite…</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-[#F7F3EE] relative overflow-hidden">
      <div className="blob w-[500px] h-[500px] bg-[#FFB86B]/40 -top-40 -left-40" />
      <div className="blob w-[400px] h-[400px] bg-[#1E5FFF]/20 bottom-0 right-0" />
      <div className="w-full max-w-md card-elevated p-8 sm:p-10 relative z-10">
        <div className="pill-badge bg-[#E0EBFF] text-[#1E5FFF] mb-4">
          {isInvite ? <><CheckCircle size={12} weight="fill" /> Convite validado</> : <><ChalkboardTeacher size={12} weight="fill" /> Novo professor</>}
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-[#12143A] leading-tight">
          {isInvite ? "Bem-vindo(a)!" : "Cadastro de professor"}
        </h1>
        <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
          {isInvite
            ? <>Você foi convidado(a) por <b>{inviteState?.teacher_name}</b>. Complete seus dados para começar.</>
            : "Crie sua conta e comece a acompanhar seus alunos. Depois, use seu Link de Convite para cadastrar sua turma."
          }
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-xs uppercase font-bold tracking-wider">Nome</Label>
            <Input data-testid="reg-name" required value={form.name}
                   onChange={(e) => setForm({...form, name: e.target.value})}
                   className="mt-2 h-12 rounded-2xl" />
          </div>
          <div>
            <Label className="text-xs uppercase font-bold tracking-wider">Email</Label>
            <Input data-testid="reg-email" type="email" required value={form.email}
                   onChange={(e) => setForm({...form, email: e.target.value})}
                   className="mt-2 h-12 rounded-2xl" />
          </div>
          <div>
            <Label className="text-xs uppercase font-bold tracking-wider">Senha</Label>
            <Input data-testid="reg-password" type="password" required minLength={6}
                   value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                   className="mt-2 h-12 rounded-2xl" />
          </div>
          {isInvite && (
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider">Perfil</Label>
              <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                <SelectTrigger data-testid="reg-role" className="mt-2 h-12 rounded-2xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Aluno(a)</SelectItem>
                  <SelectItem value="parent">Pai/Responsável</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {isInvite && form.role === "parent" && (
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider">Email do aluno vinculado</Label>
              <Input data-testid="reg-linked" type="email" required value={form.linked_student_email}
                     onChange={(e) => setForm({...form, linked_student_email: e.target.value})}
                     className="mt-2 h-12 rounded-2xl" />
            </div>
          )}
          {isInvite && form.role === "student" && (
            <>
              <div>
                <Label className="text-xs uppercase font-bold tracking-wider flex items-center gap-1.5">
                  <WhatsappLogo size={14} weight="fill" className="text-[#25D366]" />
                  Número de WhatsApp
                </Label>
                <Input data-testid="reg-phone" type="tel" required inputMode="numeric"
                       placeholder="(11) 99999-9999"
                       value={form.phone}
                       onChange={(e) => setForm({...form, phone: formatPhoneBR(e.target.value)})}
                       className="mt-2 h-12 rounded-2xl" />
                <p className="text-[11px] text-zinc-400 mt-1">Usado para lembretes de atividades e planos de estudo.</p>
              </div>
              <label htmlFor="reg-consent" className="flex items-start gap-3 p-3 rounded-2xl border border-zinc-200 bg-[#F7FAFF] cursor-pointer hover:border-[#1E5FFF]/40 transition-colors">
                <input id="reg-consent" data-testid="reg-consent" type="checkbox"
                       checked={form.whatsapp_consent}
                       onChange={(e) => setForm({...form, whatsapp_consent: e.target.checked})}
                       className="mt-1 h-4 w-4 accent-[#1E5FFF] cursor-pointer" />
                <span className="text-sm text-zinc-700 leading-snug">
                  Autorizo receber notificações de atividades e planos de estudo via WhatsApp.
                </span>
              </label>
            </>
          )}
          <Button data-testid="reg-submit" type="submit" disabled={loading}
                  className="w-full h-12 gradient-primary hover:opacity-90 text-white font-bold rounded-2xl shadow-[0_10px_30px_rgba(30,95,255,0.35)]">
            {loading ? "Criando…" : "Criar conta →"}
          </Button>
          <p className="text-sm text-zinc-500 text-center pt-2">
            Já tem conta? <Link to="/login" className="text-[#1E5FFF] font-semibold underline">Entrar</Link>
          </p>
          <p className="text-[11px] text-zinc-400 flex items-center justify-center gap-1 pt-2">
            <LockKey size={10} weight="fill" /> Sua senha é criptografada.
          </p>
        </form>
      </div>
    </div>
  );
}
